// Supabase backend: sign-in by Supabase Auth, data in Postgres behind the RLS rules of
// supabase/schema.sql. Every list is read page by page.
import { createClient, type PostgrestError } from '@supabase/supabase-js';
import type {
  Appointment, AppointmentEvent, AppointmentRow, B2cReturn, CallRow, CollectLogRow, DepotEntry, PositionRow, Profile, ProgramRow, SnapshotRow, StarRow,
  StockExchange, StockItemRow, StockRun, StopNoteRow, StopSnapshot,
} from '../../domain/types';
import type { RouteAnswer } from '../../../supabase/functions/_shared/routePayload.ts';
import type { Db } from './types';

const PAGE = 1000;

function ok<T>(r: { data: T; error: PostgrestError | Error | null }): T {
  check(r);
  return r.data;
}
function check(r: { error: { message?: string } | null }): void {
  if (r.error) throw new Error(r.error.message || String(r.error));
}

interface RangeQuery<T> {
  range(from: number, to: number): PromiseLike<{ data: T[] | null; error: PostgrestError | null }>;
}
/** Keeps going until an empty page, even when the project caps rows below PAGE. */
async function all<T>(build: () => RangeQuery<T>, onPage?: (n: number) => void): Promise<T[]> {
  let out: T[] = [], from = 0;
  for (;;) {
    const r = ok(await build().range(from, from + PAGE - 1)) || [];
    out = out.concat(r);
    if (onPage) onPage(out.length);
    if (!r.length) return out;
    from += r.length;
  }
}

/** The functions answer {error: "..."}; show that rather than the generic HTTP error. */
async function functionError(err: Error): Promise<Error> {
  const ctx = (err as { context?: Response }).context;
  let msg = err.message;
  try {
    const body = ctx && (await ctx.json());
    if (body?.error) msg = body.error;
  } catch { /* keep the generic message */ }
  return new Error(msg);
}

export function createSupabaseDb(url: string, anonKey: string): Db {
  const sb = createClient(url, anonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  });
  const redirect = () => location.origin + '/login';

  return {
    local: false,
    // ---- auth
    async getUser() {
      const r = await sb.auth.getSession();
      const u = r.data.session?.user;
      return u ? { id: u.id, email: u.email ?? null } : null;
    },
    async signIn(email, password) {
      check(await sb.auth.signInWithPassword({ email, password }));
    },
    async signUp(email, password, fullName) {
      const r = await sb.auth.signUp({ email, password, options: { data: { full_name: fullName || '' }, emailRedirectTo: redirect() } });
      check(r);
      return { needsConfirm: !r.data.session };
    },
    async signOut() {
      await sb.auth.signOut();
    },
    async resetPassword(email) {
      check(await sb.auth.resetPasswordForEmail(email, { redirectTo: redirect() }));
    },
    async updatePassword(password) {
      check(await sb.auth.updateUser({ password }));
    },
    onAuth(cb) {
      const { data } = sb.auth.onAuthStateChange((ev) => cb(ev));
      return () => data.subscription.unsubscribe();
    },
    // ---- team
    async myProfile(uid) {
      return ok(await sb.from('profiles').select('*').eq('id', uid).maybeSingle()) as Profile | null;
    },
    async listProfiles() {
      return (ok(await sb.from('profiles').select('*').order('created_at')) || []) as Profile[];
    },
    async updateProfile(id, patch) {
      ok(await sb.from('profiles').update(patch).eq('id', id));
    },
    // ---- appointments
    fetchAppointments(since, onPage) {
      return all<AppointmentRow>(() => {
        let q = sb.from('appointments').select('id,raw,changed_at').order('changed_at').order('id');
        if (since) q = q.gte('changed_at', since);
        return q as unknown as RangeQuery<AppointmentRow>;
      }, onPage);
    },
    async insertSnapshot(s) {
      const r = ok(await sb.from('snapshots').insert(s).select('id').single()) as { id: number };
      return r.id;
    },
    async upsertAppointments(rows) {
      ok(await sb.from('appointments').upsert(rows, { onConflict: 'id' }));
    },
    listSnapshots() {
      return all<SnapshotRow>(() => sb.from('snapshots').select('*').order('uploaded_at') as unknown as RangeQuery<SnapshotRow>);
    },
    listEvents() {
      return all<AppointmentEvent>(
        () => sb.from('appointment_events').select('appointment_id,old_status,new_status,source_updated_at,seen_at').order('id') as unknown as RangeQuery<AppointmentEvent>,
      );
    },
    // ---- programs
    async listPrograms() {
      const progs = await all<ProgramRow>(() => sb.from('programs').select('*').order('created_at') as unknown as RangeQuery<ProgramRow>);
      const stops = await all<{ program_id: string; snapshot: StopSnapshot }>(
        () => sb.from('program_stops').select('program_id,appointment_id,position,snapshot').order('program_id').order('position') as unknown as RangeQuery<{ program_id: string; snapshot: StopSnapshot }>,
      );
      const by = new Map<string, ProgramRow>();
      progs.forEach((p) => {
        p.stops = [];
        by.set(p.id, p);
      });
      stops.forEach((s) => by.get(s.program_id)?.stops.push(s.snapshot));
      return progs;
    },
    async savePrograms(progs, deletedIds) {
      ok(await sb.rpc('save_programs', { progs, deleted_ids: deletedIds || [] }));
    },
    async updateProgram(id, patch) {
      ok(await sb.from('programs').update(patch).eq('id', id));
    },
    async roadTrip(r) {
      const res = await sb.functions.invoke<RouteAnswer>('route', { method: 'POST', body: r });
      if (res.error) throw await functionError(res.error);
      return res.data!;
    },
    async closeProgram(id, driver) {
      ok(await sb.rpc('close_program', { p_id: id, p_driver: driver }));
    },
    // ---- driver sheets
    listReturns() {
      return all<B2cReturn>(() => sb.from('b2c_returns').select('*').order('program_id') as unknown as RangeQuery<B2cReturn>);
    },
    async replaceReturns(programId, rows) {
      ok(await sb.from('b2c_returns').delete().eq('program_id', programId));
      if (rows.length) ok(await sb.from('b2c_returns').insert(rows));
    },
    // ---- positions
    listPositions() {
      return all<PositionRow>(() => sb.from('positions').select('*').order('appointment_id') as unknown as RangeQuery<PositionRow>);
    },
    async setPosition(id, lat, lng) {
      ok(await sb.from('positions').upsert({ appointment_id: id, lat, lng }, { onConflict: 'appointment_id' }));
    },
    async deletePosition(id) {
      ok(await sb.from('positions').delete().eq('appointment_id', id));
    },
    // ---- calls to customers
    listCalls() {
      return all<CallRow>(() => sb.from('calls').select('*').order('id') as unknown as RangeQuery<CallRow>);
    },
    async addCall(appointmentId, outcome, note) {
      return ok(await sb.from('calls').insert({ appointment_id: appointmentId, outcome, note }).select('*').single()) as CallRow;
    },
    async deleteCall(id) {
      ok(await sb.from('calls').delete().eq('id', id));
    },
    // ---- notes on stops
    listStopNotes() {
      return all<StopNoteRow>(() => sb.from('stop_notes').select('*').order('appointment_id') as unknown as RangeQuery<StopNoteRow>);
    },
    async setStopNote(appointmentId, note, byName) {
      if (!note) {
        ok(await sb.from('stop_notes').delete().eq('appointment_id', appointmentId));
        return null;
      }
      const row = { appointment_id: appointmentId, note, noted_by_name: byName, noted_at: new Date().toISOString() };
      return ok(await sb.from('stop_notes').upsert(row, { onConflict: 'appointment_id' }).select('*').single()) as StopNoteRow;
    },
    // ---- starred customers
    listStars() {
      return all<StarRow>(() => sb.from('starred_customers').select('*').order('client_key') as unknown as RangeQuery<StarRow>);
    },
    async addStar(clientKey) {
      ok(await sb.from('starred_customers').upsert({ client_key: clientKey }, { onConflict: 'client_key' }));
    },
    async deleteStar(clientKey) {
      ok(await sb.from('starred_customers').delete().eq('client_key', clientKey));
    },
    // ---- the UCO app's API, through the Edge Functions
    async fetchFromApi() {
      const r = await sb.functions.invoke<{ data: unknown }>('import-from-api', { method: 'POST' });
      if (r.error) throw await functionError(r.error);
      return r.data?.data ?? null;
    },
    // ---- stock of gifts
    listDepot() {
      return all<DepotEntry>(() => sb.from('stock_depot').select('*').order('id') as unknown as RangeQuery<DepotEntry>);
    },
    async addDepot(kind, items, note, byName) {
      return ok(await sb.from('stock_depot').insert({ kind, items, note, done_by_name: byName }).select('*').single()) as DepotEntry;
    },
    listRuns() {
      return all<StockRun>(() => sb.from('stock_runs').select('*').order('id') as unknown as RangeQuery<StockRun>);
    },
    async loadRun(r) {
      return ok(await sb.from('stock_runs').insert(r).select('*').single()) as StockRun;
    },
    async returnRun(id, returned, note, byName) {
      const patch = { returned, note, returned_by: (await sb.auth.getUser()).data.user?.id ?? null, returned_by_name: byName, returned_at: new Date().toISOString() };
      return ok(await sb.from('stock_runs').update(patch).eq('id', id).select('*').single()) as StockRun;
    },
    async updateDepot(id, items, note, byName) {
      ok(await sb.from('stock_depot').update({ items, note, edited_by_name: byName, edited_at: new Date().toISOString() }).eq('id', id));
    },
    async deleteDepot(id) {
      ok(await sb.from('stock_depot').delete().eq('id', id));
    },
    async updateRun(id, patch, byName) {
      ok(await sb.from('stock_runs').update({ ...patch, edited_by_name: byName, edited_at: new Date().toISOString() }).eq('id', id));
    },
    async deleteRun(id) {
      ok(await sb.from('stock_runs').delete().eq('id', id));
    },
    listExchanges() {
      return all<StockExchange>(() => sb.from('stock_exchanges').select('*').order('id') as unknown as RangeQuery<StockExchange>);
    },
    async addExchange(e) {
      ok(await sb.from('stock_exchanges').insert(e));
    },
    async updateExchange(id, e, byName) {
      ok(await sb.from('stock_exchanges').update({ ...e, edited_by_name: byName, edited_at: new Date().toISOString() }).eq('id', id));
    },
    async deleteExchange(id) {
      ok(await sb.from('stock_exchanges').delete().eq('id', id));
    },
    async listStockItems() {
      return (ok(await sb.from('stock_items').select('*').order('name')) || []) as StockItemRow[];
    },
    async saveStockItem(r) {
      ok(await sb.from('stock_items').upsert({ ...r, updated_at: new Date().toISOString() }, { onConflict: 'item_id' }));
    },
    async listCollectors() {
      return (ok(await sb.rpc('list_collectors')) || []) as { id: string; name: string }[];
    },
    listCollectLog() {
      return all<CollectLogRow>(() => sb.from('collect_log').select('*').order('id') as unknown as RangeQuery<CollectLogRow>);
    },
    async updateAppointment(u) {
      const r = await sb.functions.invoke<{ raw: Appointment; saved: boolean; saveError: string | null; logError?: string | null }>('update-appointment', { method: 'POST', body: u });
      if (r.error) throw await functionError(r.error);
      if (!r.data?.saved) console.warn('update-appointment: the UCO app has the change, our copy does not yet:', r.data?.saveError);
      if (r.data?.logError) console.warn('update-appointment: the change is not in the collect log:', r.data.logError);
      return r.data!.raw;
    },
  };
}
